import {
  AfterViewChecked,
  Component,
  ElementRef,
  Injector,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import ClassicEditor from '@ckeditor-build/ckeditor.js';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CommonConstants } from '../../../shared/common.constant';
import {
  CitationService,
  ContextMenuEditorService,
  WkInsertWebEmailLinkService,
  WkKeywordService,
  WkTranslationService,
} from '../../../services';
import { CKEditorComponent } from '../../ckeditor/ckeditor.component';
import { InsertLinkEvent, RouteToLinkEvent } from '../../../model/event.model';
import { WKInsertLinkComponent } from '../../../dialogs/wk-insert-link/wk-insert-link.component';
import { CKEditor5 } from '../../ckeditor';
import { AppStore } from '../../../store/states';
import { SetDefinitionEditorAction } from '../../../store/actions/app.custom-actions';

@Component({
  selector: 'wk-definition-editor',
  templateUrl: './wk-definition-editor.component.html',
  styleUrls: ['./wk-definition-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class WkDefinitionEditorComponent implements OnInit, AfterViewChecked {
  @ViewChild(CKEditorComponent)
  definitionEditor: CKEditorComponent;
  wkDefinitionEditor = ClassicEditor;
  definitionPlugins: {};
  definitionEditorData: string;
  setClickDetails: RouteToLinkEvent;
  loadContextMenuComponent = false;
  contextMenuObject: any;
  ckEditorEditableElement: any;
  rightClickEventDetails;
  @Input() definitionEditorString: string;

  private readonly translate: WkTranslationService;
  private readonly dialog: MatDialog;
  private readonly wkInsertWebEmailLinkService: WkInsertWebEmailLinkService;
  private readonly elementRef: ElementRef;
  private readonly renderer: Renderer2;
  private readonly onDestroy$ = new Subject<void>();
  private readonly contextmenuService: ContextMenuEditorService;
  private readonly citationService: CitationService;
  private readonly keywordService: WkKeywordService;
  constructor(injector: Injector, private readonly store: Store<AppStore>) {
    this.translate = injector.get(WkTranslationService);
    this.dialog = injector.get(MatDialog);
    this.wkInsertWebEmailLinkService = injector.get(
      WkInsertWebEmailLinkService
    );
    this.elementRef = injector.get(ElementRef);
    this.renderer = injector.get(Renderer2);
    this.contextmenuService = injector.get<ContextMenuEditorService>(
      ContextMenuEditorService
    );
    this.citationService = injector.get(CitationService);
    this.keywordService = injector.get(WkKeywordService);
  }

  ngOnInit(): void {
    this.getToolBarItemsForEditors(
      CommonConstants.miniEditorPlugins.toolbar.items
    );
    this.setRightClickEventDetails();
    this.getMiniEditorDetails();
    this.onEventDetails();
  }

  ngAfterViewChecked() {
    this.setEditorData();
  }

  async setEditorData() {
    if (
      this.elementRef?.nativeElement?.querySelector(
        CommonConstants.ckEditorEditablePlaceholder
      )?.firstChild
    ) {
      this.renderer.listen(
        this.elementRef.nativeElement.querySelector(
          CommonConstants.ckEditorEditablePlaceholder
        ).firstChild,
        'contextmenu',
        this.openContextMenuClick.bind(this)
      );
    }
  }

  getToolBarItemsForEditors(toolbarItems: string[]) {
    this.definitionPlugins = {
      toolbar: {
        items: toolbarItems,
      },
      placeholder: this.translate.instant(
        `APP.KEYWORDS_DEFINITION.DEFINITION_PLACEHOLDER`
      ),
    };
  }

  onReady() {
    this.definitionEditorData = this.definitionEditorString || '';
    this.definitionEditor.editorInstance.commands.get('cut').isEnabled = false;
    this.ckEditorEditableElement = this.elementRef.nativeElement.querySelector(
      `.${CommonConstants.editorEditableInline}`
    );
    this.renderer.listen(
      this.elementRef.nativeElement.querySelector(
        CommonConstants.ckEditorEditablePlaceholder
      ),
      'mousedown',
      this.onMouseEventInCK5Editor.bind(this)
    );
  }

  onMouseEventInCK5Editor(event) {
    this.citationService.miniEditorCoords = {
      top: event.pageY,
      left: event.pageX,
      clientX: event.clientX,
      marginLeft: getComputedStyle(this.ckEditorEditableElement).marginLeft,
      marginRight: getComputedStyle(this.ckEditorEditableElement).marginRight,
      offsetHeight:
        event.pageY -
        this.elementRef.nativeElement.querySelector(
          CommonConstants.ckEditorMainClassName
        ).offsetHeight,
    };
  }

  onChange() {
    this.store.dispatch(
      new SetDefinitionEditorAction(this.definitionEditorData)
    );
    this.definitionEditor.editorInstance.focus();
  }

  onInsertLink(insertLinkEvent: InsertLinkEvent) {
    this.keywordService.setkeyDefinitionEditType(this.definitionEditor);
    const dialog = this.dialog.open(WKInsertLinkComponent, {
      maxHeight: '400vh',
      panelClass: 'wk-insert-media-dialog',
      width: '80%',
    });
    dialog.componentInstance?.setLinkEvent(insertLinkEvent);
  }

  openContextMenu(event) {
    this.citationService.setRightClickEventDetails.next(
      CommonConstants.editor.DEFINITION_EDITOR
    );
    this.loadContextMenuComponent = true;
    this.contextMenuObject = this.contextmenuService.removeElementsByName(
      event.contextMenuObj,
      [CommonConstants.rightClickOptions.LINK_TO]
    );
  }

  openContextMenuClick(event) {
    event.target?.click();
    this.definitionEditor.editorInstance.execute('contextmenu', {
      value: event,
    });
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  runContextMenuAction(data) {
    if (data.eventName === CommonConstants.rightClickOptions.goToLink) {
      this.citationService.routeToLink(this.elementRef, this.setClickDetails);
    }
    if (data.eventName === CommonConstants.rightClickOptions.editLink) {
      let linkAddress = data.actionDetails.linkAddress;
      if (linkAddress.indexOf('mailto:') === 0) {
        linkAddress = linkAddress.replace('mailto:', '');
      }

      const linkElement = this.elementRef.nativeElement?.querySelector(
        `[id='${data.actionDetails.idUnique}']`
      );
      const insertLinkData: InsertLinkEvent = {
        editor: this.definitionEditor.editorInstance,
        arguments: {
          source: data.actionDetails,
        } as CKEditor5.EventInfo<'insertlink'>,
        linkAttributes: {
          linkText: data.actionDetails.linkText,
          linkAddress,
          linkElement,
        },
      };
      this.onInsertLink(insertLinkData);
    }
    if (data.eventName === CommonConstants.rightClickOptions.deleteLink) {
      const linkProperties = {
        linkText: data.actionDetails.linkText,
        linkAddress: data.actionDetails.linkAddress,
      };
      this.definitionEditor.editorInstance.execute(
        'deletewebemaillink',
        linkProperties
      );
    }
  }

  routeToLink(event: RouteToLinkEvent) {
    this.wkInsertWebEmailLinkService.setMiniEditorDetails(event);
    this.citationService.setRightClickEventDetails.next(
      CommonConstants.editor.DEFINITION_EDITOR
    );
  }

  private setRightClickEventDetails(): void {
    this.citationService.setRightClickEventDetails
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => {
        this.rightClickEventDetails = data;
      });
  }

  private getMiniEditorDetails(): void {
    this.wkInsertWebEmailLinkService
      .getMiniEditorDetails()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data: RouteToLinkEvent) => {
        this.setClickDetails = data;
      });
  }

  private onEventDetails(): void {
    this.contextmenuService
      .getEventDetails()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => {
        if (
          data &&
          this.rightClickEventDetails ===
            CommonConstants.editor.DEFINITION_EDITOR
        ) {
          this.runContextMenuAction(data);
        }
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.contextmenuService.setEventDetails('');
  }
}
